/** Accounts awaiting sector assignment, with a searchable and filterable directory in a modal. */
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { STATUS_LOADING, STATUS_SAVING } from "@/redux/_status.types"
import { BENCHMARK_SECTOR_LIST } from "@/redux/benchmark.types"
import Link from "@/components/link"
import ListButton from "@/components/list-button"
import ListItem from "@/components/list-item"
import ListCell from "@/components/list-cell"
import Checkbox from "@/components/checkbox"
import Modal from "@/components/modal"
import SupervisorAccountConnectModal from "@/modals/supervisor-account-connect.modal"
import PageLoader from "@/components/page-loader"
import SearchBar from "@/components/search-bar"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faTriangleExclamation,
  faChevronRight,
  faArrowRight,
  faLayerGroup,
  faBuilding,
  faCircleQuestion,
  faPlayCircle,
  faTrash
} from "@fortawesome/free-solid-svg-icons"
import { BenchmarkAccountRow, purgeBenchmarkAccounts_AsSupervisor, PurgeBenchmarkAccountsResult } from "@/features/benchmark/entities/account/model/benchmarkAccount.thunk"
import { SECTOR_ICONS } from "@/features/benchmark/entities/question/ui/modals/SectorGrid"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"

interface Props {
  accounts: BenchmarkAccountRow[]
  isLoaded: boolean
  hasError: boolean
  onRetry: () => void
  onOpenAccount: (account: BenchmarkAccountRow) => void
  isAccountPickerOpen: boolean
  maxShown: number
  onAccountsPurged: (result: PurgeBenchmarkAccountsResult) => void
}

export default function SupervisorAccountsWithoutSectorWidget(props: Props) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const canPurge = useAppSelector((state) => state._session.supervisorLevel >= 2)
  // Same color the "play" connect button already uses on each row (ListButton isPrimary ->
  // accountColors.cta) — read live instead of duplicating its resolved hex, so a future
  // branding/theme change stays in sync automatically.
  const ctaColor = useAppSelector((state) => state._session.accountColors.cta)
  const [pendingPurge, setPendingPurge] = useState<string[] | null>(null)
  const [isPurging, setIsPurging] = useState(false)
  const [purgeMessage, setPurgeMessage] = useState("")
  const [purgeError, setPurgeError] = useState("")

  async function confirmPurge() {
    if (!pendingPurge || isPurging) return
    setIsPurging(true)
    setPurgeError("")
    try {
      const result = await dispatch(purgeBenchmarkAccounts_AsSupervisor(pendingPurge)).unwrap()
      props.onAccountsPurged(result)
      setPendingPurge(null)
      setPurgeMessage(`${result.deletedIds.length} compte(s) et ${result.deletedFacts} ligne(s) de faits supprimés.` +
        (result.skippedIds.length ? ` ${result.skippedIds.length} compte(s) conservés : encore présents dans l’application, liés à un rapport ou déjà absents des archives.` : "") +
        (result.benchmarksInvalidated ? " Relancez Calculer pour actualiser les benchmarks." : ""))
    } catch {
      setPurgeError("La suppression a échoué. Vous pouvez réessayer.")
    } finally {
      setIsPurging(false)
    }
  }
  const [connectingAccount, setConnectingAccount] = useState<BenchmarkAccountRow | null>(null)
  const [showAll, setShowAll] = useState(false)
  const [search, setSearch] = useState("")
  const [modalSearch, setModalSearch] = useState("")
  const [onlyDeleted, setOnlyDeleted] = useState(false)
  const [sectorFilter, setSectorFilter] = useState<string | null>(null)

  if (!props.isLoaded) return <PageLoader status={STATUS_LOADING} minHeight={120} />

  // A deleted account (gone from app.Accounts, isDeleted derived from that absence — see
  // getBenchmarkAccounts()) has nothing left to assign a sector to, and shouldn't clutter this
  // reminder at all — not just get a "Compte supprimé" tag while still showing up (found live,
  // 2026-09-22). Only "Comptes supprimés uniquement" checked opts back into seeing them.
  const activeAccounts = props.accounts.filter((account) => !account.isDeleted)
  const withoutSector = activeAccounts.filter((account) => !account.sector)
  const hasWarning = withoutSector.length > 0
  const previewAccounts = (search.trim() ? activeAccounts : hasWarning ? withoutSector : activeAccounts).filter(
    (account) => account.account.toLowerCase().includes(search.trim().toLowerCase())
  )
  const filteredAccounts = (onlyDeleted ? props.accounts.filter((account) => account.isDeleted) : activeAccounts).filter(
    (account) =>
      account.account.toLowerCase().includes(modalSearch.trim().toLowerCase()) &&
      (sectorFilter === null || (sectorFilter === "unassigned" ? !account.sector : account.sector === sectorFilter))
  )

  function renderAccount(account: BenchmarkAccountRow, withConnect = false) {
    // Same badge, different breathing room per layout: inline next to the name in the wide modal
    // (marginLeft), but stacked on its own wrapped line in the narrow compact card, where a left
    // offset would just indent that line instead of spacing it from the name above (marginTop).
    const nameAndDeletedBadge = (
      <>
        <span style={{ overflowWrap: "anywhere" }}>{account.account}</span>
        {account.isDeleted && (
          <span
            className="flex items-center gap-2 text-sm red-t"
            style={{ whiteSpace: "nowrap", ...(withConnect ? { marginLeft: 8 } : { marginTop: 4 }) }}>
            <FontAwesomeIcon icon={faTrash} fixedWidth />
            {t("benchmark_supervisor_account_deleted")}
          </span>
        )}
      </>
    )
    const sectorBadge = (
      <span
        className="flex items-center gap-1 text-sm"
        style={{ color: account.sector ? undefined : "#ff9500", whiteSpace: "nowrap" }}>
        <FontAwesomeIcon icon={account.sector ? SECTOR_ICONS[account.sector] || faBuilding : faTriangleExclamation} fixedWidth />
        {account.sector ? t("benchmark_sector_" + account.sector, { defaultValue: account.sector }) : "Sans secteur"}
      </span>
    )

    // Two different layouts, not one shared one forced into both shapes (found live, 2026-09-17
    // — a first attempt used the same fixed-width ListCell columns for both, sized for the wide
    // "Voir tout" modal; in the much narrower sidebar card, reserving that much fixed width for
    // the trailing columns left barely any room for the name, and overflow-wrap:anywhere took
    // that down to wrapping one letter per line instead of just truncating).
    //
    // withConnect (the wide modal): a deleted account skips the connect ListButton entirely, so
    // the sector badge's left edge drifted row to row depending on whether a button followed it —
    // fixed-width ListCells give every row the same trailing columns regardless of what's inside.
    if (withConnect) {
      return (
        <ListItem key={account.id} isSmall isEditable className="items-center gap-2" onClick={() => props.onOpenAccount(account)}>
          <ListCell isFlex>
            <div className="flex flex-wrap items-center gap-2">{nameAndDeletedBadge}</div>
          </ListCell>
          {/* 160px wrapped "Médias & Communication"/"Bailleur social & Logement" onto 2 lines
              even though the wide modal has plenty of room to spare (found live, 2026-09-17) —
              240px plus nowrap on the badge itself fits the longest sector label on one line. */}
          <ListCell width={240} className="justify-end">
            {sectorBadge}
          </ListCell>
          <ListCell width={130} className="justify-end">
            {!account.isDeleted && (
              <ListButton icon={faPlayCircle} isPrimary text={t("utils_connect")} onClick={() => setConnectingAccount(account)} />
            )}
          </ListCell>
        </ListItem>
      )
    }

    // The compact sidebar card: the trailing slot is always a chevron, never conditionally
    // absent — the original proportional layout (sector badge capped at 50% instead of a fixed
    // pixel width) already kept these rows aligned, nothing to fix here.
    return (
      <ListItem key={account.id} isSmall isEditable className="items-center gap-2" onClick={() => props.onOpenAccount(account)}>
        <div className="flex flex-wrap items-center gap-2" style={{ flex: 1, minWidth: 0 }}>{nameAndDeletedBadge}</div>
        <div style={{ maxWidth: "50%" }}>{sectorBadge}</div>
        <FontAwesomeIcon icon={faChevronRight} className="grey-t" style={{ fontSize: 14, flexShrink: 0 }} />
      </ListItem>
    )
  }

  return (
    <>
      <div
        style={{
          border: hasWarning ? "2px solid #ff9500" : "1px solid #E5E5E5",
          borderRadius: 8,
          overflow: "hidden",
          backgroundColor: "#fff",
          boxShadow: "0px 4px 24px rgba(0,0,0,0.1)"
        }}>
        <div
          className="flex items-center gap-2"
          style={{
            backgroundColor: hasWarning ? "#ff9500" : undefined,
            color: hasWarning ? "#fff" : undefined,
            padding: "16px 24px"
          }}>
          {hasWarning && <FontAwesomeIcon icon={faTriangleExclamation} />}
          <b>
            {hasWarning
              ? t("benchmark_supervisor_accounts_without_sector", { count: withoutSector.length })
              : t("accounts")}
          </b>
        </div>

        {props.hasError ? (
          <div role="alert" style={{ padding: "14px 24px" }}>
            <p>Impossible de charger les comptes.</p>
            <Link isWithoutMargin onClick={props.onRetry}>
              Réessayer
            </Link>
          </div>
        ) : (
          <>
            <div style={{ padding: "14px 24px" }}>
              <SearchBar searchText={search} onSearch={setSearch} placeholder={t("utils_search")} />
            </div>
            {previewAccounts.slice(0, props.maxShown).map((account) => renderAccount(account))}
            {previewAccounts.length === 0 && (
              <div className="grey-t" style={{ padding: "14px 24px" }}>
                {t("utils_no_results")}
              </div>
            )}
            <div style={{ padding: "14px 24px", borderTop: "1px solid #F0F0F0" }}>
              <Link
                isWithoutMargin
                icon={faArrowRight}
                onClick={() => {
                  setModalSearch(search)
                  setSectorFilter(null)
                  setOnlyDeleted(false)
                  setShowAll(true)
                }}>
                {t("benchmark_supervisor_accounts_see_all", { count: props.accounts.length })}
              </Link>
            </div>
          </>
        )}
      </div>

      {showAll && !props.isAccountPickerOpen && !connectingAccount && !pendingPurge && (
        <Modal isFullScreen title={t("accounts")} isCloseButtonVisible onClose={() => setShowAll(false)}>
          {/* Admin screen, not the client-facing account page — optimized for vertical density
              (max rows visible below), per Tom, 2026-09-22. Tried moving search into Modal's own
              title row (it has one via onSearch) to reclaim that row's height — reverted, it read
              as disconnected (title left, search far right, empty middle) rather than saving
              space. Back to search + checkboxes on one line. */}
          <div style={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "#fff", paddingBottom: 12 }}>
            <div className="flex flex-wrap items-center gap-4" style={{ marginBottom: 16 }}>
              <SearchBar searchText={modalSearch} onSearch={setModalSearch} placeholder={t("utils_search")} />
              {/* Checkbox's own CSS has `margin: auto` (checkbox-container) — as a direct flex
                  child it absorbs all the row's free space instead of just sitting next to the
                  search bar (same quirk already found on SupervisorBenchmarkQuestions.tsx's own
                  checkbox column). Plain wrapper div breaks that direct-flex-child relationship. */}
              <div>
                <Checkbox active={onlyDeleted} text="Comptes supprimés uniquement" onClick={({ value }) => setOnlyDeleted(value)} />
              </div>
              {onlyDeleted && canPurge && filteredAccounts.length > 0 && (
                <Link isWithoutMargin onClick={() => {
                  setPurgeError("")
                  setPendingPurge(filteredAccounts.filter(account => account.isDeleted).map(account => account.id))
                }}>
                  Supprimer définitivement
                </Link>
              )}
            </div>

            {/* Sector picker: small pills wrapping in one strip instead of a tall padded grid —
                ListButton's own `text` prop is tooltip-only (checked its source), so bare icons
                had nothing to show; built as plain buttons with a visible label instead, sized
                for density rather than the card look. Active state uses ctaColor (same blue as
                each row's own "play" connect button). */}
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-3" style={{ marginBottom: 16 }}>
              {[
                { key: null, label: "Tous les secteurs", icon: faLayerGroup },
                ...BENCHMARK_SECTOR_LIST.map((sector) => ({ key: sector, label: t("benchmark_sector_" + sector), icon: SECTOR_ICONS[sector] })),
                { key: "unassigned", label: "Secteur non défini", icon: faCircleQuestion }
              ].map((item) => {
                const isActive = sectorFilter === item.key
                // "Secteur non défini" always reads as a warning, active or not — same #ff9500
                // already used for each row's own "Sans secteur" badge (sectorBadge above),
                // rather than the neutral ctaColor every other pill uses.
                const color = item.key === "unassigned" ? "#ff9500" : ctaColor
                return (
                  <button
                    key={item.key ?? "all"}
                    onClick={() => setSectorFilter(item.key === null ? null : sectorFilter === item.key ? null : item.key)}
                    className={"flex items-center gap-1.5 text-sm transition-colors " + (isActive ? "" : "hover:bg-gray-50")}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 999,
                      border: `1px solid ${isActive ? color : "#E4E7EC"}`,
                      backgroundColor: isActive ? `${color}1A` : "#fff",
                      color: isActive || item.key === "unassigned" ? color : undefined,
                      fontWeight: isActive ? 600 : 400,
                      cursor: "pointer"
                    }}>
                    <FontAwesomeIcon icon={item.icon} fixedWidth />
                    {item.label}
                  </button>
                )
              })}
            </div>

            <div className="grey-t text-sm" aria-live="polite">
              {filteredAccounts.length} / {props.accounts.length} {t("accounts").toLowerCase()}
              {sectorFilter &&
                ` · ${sectorFilter === "unassigned" ? "Secteur non défini" : t("benchmark_sector_" + sectorFilter)}`}
            </div>
          </div>
          {purgeMessage && <p role="status">{purgeMessage}</p>}
          {filteredAccounts.map((account) => renderAccount(account, true))}
          {filteredAccounts.length === 0 && (
            <div className="grey-t" style={{ padding: "24px" }}>
              {t("utils_no_results")}
            </div>
          )}
        </Modal>
      )}
      {pendingPurge && (
        <Modal
          title="Supprimer définitivement"
          isCloseButtonVisible={!isPurging}
          disableKeyEvents={isPurging}
          disableClickOutside={isPurging}
          onClose={() => { if (!isPurging) setPendingPurge(null) }}
          onNext={confirmPurge}
          onNextCta="Supprimer définitivement"
          nextDisabled={isPurging}
          status={isPurging ? STATUS_SAVING : undefined}>
          <p>Supprimer les archives des <b>{pendingPurge.length} comptes</b> affichés par les filtres ?</p>
          <p>Leurs réponses, invitations et messages archivés seront définitivement supprimés.
            Les comptes encore présents dans l’application ou liés à un rapport seront conservés.</p>
          <p>Les benchmarks calculés seront invalidés si des faits sont supprimés. Il faudra relancer « Calculer ».</p>
          {purgeError && <p role="alert" className="red-t">{purgeError}</p>}
        </Modal>
      )}
      {connectingAccount && (
        <SupervisorAccountConnectModal
          account={{ id: connectingAccount.id, name: connectingAccount.account }}
          onClose={() => setConnectingAccount(null)}
        />
      )}
    </>
  )
}
